import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useParams, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider, Helmet } from "react-helmet-async";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import AuthCheckWrapper from "./components/auth/AuthCheckWrapper";
import { AuthProvider } from "./hooks/useAuth";
import { EmailSyncManager } from "./components/auth/EmailSyncManager";
import LoadingSpinner from "./components/world/LoadingSpinner";
import { redirectToCanonical } from "./utils/urlUtils";

import Index from "./pages/Index";
import { EnhancedSEO } from "@/components/EnhancedSEO";
import EnhancedPerformanceOptimizer from "@/components/EnhancedPerformanceOptimizer";
import "./index.css";
import ErrorBoundary from "./components/core/ErrorBoundary";
import RouteThemeTracker from "./components/RouteThemeTracker";

// ✅ Lazy load pages
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Math = lazy(() => import("./pages/Math"));
const Alphabet = lazy(() => import("./pages/Alphabet"));
const Geography = lazy(() => import("./pages/Geography"));
const NorthAmerica = lazy(() => import("./pages/NorthAmerica"));
const SouthAmerica = lazy(() => import("./pages/SouthAmerica"));
const Europe = lazy(() => import("./pages/Europe"));
const Africa = lazy(() => import("./pages/Africa"));
const Asia = lazy(() => import("./pages/Asia"));
const Australia = lazy(() => import("./pages/Australia"));
const Antarctica = lazy(() => import("./pages/Antarctica"));
const Science = lazy(() => import("./pages/Science"));
const Stories = lazy(() => import("./pages/Stories"));
const StoryDetail = lazy(() => import("./pages/StoryDetail"));
const StoryReading = lazy(() => import("./pages/StoryReading"));
const Games = lazy(() => import("./pages/Games"));
const BrainTeasers = lazy(() => import("./pages/BrainTeasers"));
const EnglishLearning = lazy(() => import("./pages/EnglishLearning"));
const Spellings = lazy(() => import("./pages/Spellings"));
const EnglishGrammar = lazy(() => import("./pages/EnglishGrammar"));
const SentenceBuilding = lazy(() => import("./pages/SentenceBuilding"));
const SentencesOfTheDay = lazy(() => import("./pages/SentencesOfTheDay"));
const SightWords = lazy(() => import("./pages/SightWords"));
const DailyActivities = lazy(() => import("./pages/DailyActivities"));
const MeasurementBasics = lazy(() => import("./pages/MeasurementBasics"));
const ShapesPatterns = lazy(() => import("./pages/ShapesPatterns"));
const LearnColors = lazy(() => import("./pages/LearnColors"));
const TimeMoney = lazy(() => import("./pages/TimeMoney"));
const CommunityHelpers = lazy(() => import("./pages/CommunityHelpers"));
const GKHistory = lazy(() => import("./pages/GKHistory"));
const History = lazy(() => import("./pages/History"));
const LearnAboutWorld = lazy(() => import("./pages/LearnAboutWorld"));
const WorldExplorer = lazy(() => import("./pages/WorldExplorer"));
const PrintablesZone = lazy(() => import("./pages/PrintablesZone"));
import Pricing from "./pages/Pricing";
const YtGallery = lazy(() => import("./pages/YtGallery"));
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MagicLinkCallback = lazy(() => import("./pages/MagicLinkCallback"));
const ParentDashboard = lazy(() => import("./pages/ParentDashboard"));
const SubjectDetails = lazy(() => import("./pages/SubjectDetails"));

// ✅ CanonicalRoute wrapper
function CanonicalRoute({ path, element }: { path: string; element: JSX.Element }) {
  return (
    <Route
      path={path}
      element={
        <>
          <Helmet>
            <link rel="canonical" href={`https://tinyminds.in${path}`} />
          </Helmet>
          {element}
        </>
      }
    />
  );
}

// ✅ Helper for static pages
function withCanonical(path: string, element: JSX.Element) {
  return (
    <>
      <Helmet>
        <link rel="canonical" href={`https://tinyminds.in${path}`} />
      </Helmet>
      {element}
    </>
  );
}


// ✅ Dynamic canonical for blog
function BlogCanonicalWrapper() {
  const { slug } = useParams<{ slug: string }>();
  
  const canonicalUrl = slug
  ? `https://tinyminds.in/blog/${slug}`
  : "https://tinyminds.in/blog";

  return (
    <>
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <Suspense fallback={<LoadingSpinner />}>
        <BlogDetail />
      </Suspense>
    </>
  );
}


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 0,
      retry: 1,
      refetchOnWindowFocus: true,
      refetchOnMount: "always",
    },
  },
});

function App() {
  // Handle client-side URL canonicalization
  useEffect(() => {
    redirectToCanonical();
  }, []);

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <EnhancedSEO />
        <EnhancedPerformanceOptimizer />
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <EmailSyncManager />
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <RouteThemeTracker />
                <AuthCheckWrapper>
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      <Route path="/" element={withCanonical("/", <Index />)} />
                      <Route path="/about" element={withCanonical("/about", <About />)} />
                      <Route path="/contact" element={withCanonical("/contact", <Contact />)} />
                      <Route path="/math" element={withCanonical("/math", <Math />)} />
                      <Route path="/alphabet" element={withCanonical("/alphabet", <Alphabet />)} />
                      <Route path="/geography" element={withCanonical("/geography", <Geography />)} />
                      <Route path="/geography/north-america" element={withCanonical("/geography/north-america", <NorthAmerica />)} />
                      <Route path="/geography/south-america" element={withCanonical("/geography/south-america", <SouthAmerica />)} />
                      <Route path="/geography/europe" element={withCanonical("/geography/europe", <Europe />)} />
                      <Route path="/geography/africa" element={withCanonical("/geography/africa", <Africa />)} />
                      <Route path="/geography/asia" element={withCanonical("/geography/asia", <Asia />)} />
                      <Route path="/geography/australia" element={withCanonical("/geography/australia", <Australia />)} />
                      <Route path="/geography/antarctica" element={withCanonical("/geography/antarctica", <Antarctica />)} />
                      {/* Redirect old continent routes to geography paths */}
                      <Route path="/north-america" element={<Navigate to="/geography/north-america" replace />} />
                      <Route path="/south-america" element={<Navigate to="/geography/south-america" replace />} />
                      <Route path="/europe" element={<Navigate to="/geography/europe" replace />} />
                      <Route path="/africa" element={<Navigate to="/geography/africa" replace />} />
                      <Route path="/asia" element={<Navigate to="/geography/asia" replace />} />
                      <Route path="/australia" element={<Navigate to="/geography/australia" replace />} />
                      <Route path="/antarctica" element={<Navigate to="/geography/antarctica" replace />} />
                      <Route path="/science" element={withCanonical("/science", <Science />)} />
                      <Route path="/stories" element={withCanonical("/stories", <Stories />)} />
                      <Route path="/stories/:slug" element={<StoryDetail />} />

                      <Route path="/story-reading" element={withCanonical("/story-reading", <StoryReading />)} />
                      <Route path="/games" element={withCanonical("/games", <Games />)} />
                      <Route path="/spellings" element={withCanonical("/spellings", <Spellings />)} />
                      <Route path="/brain-teasers" element={withCanonical("/brain-teasers", <BrainTeasers />)} />
                      <Route path="/english-learning" element={withCanonical("/english-learning", <EnglishLearning />)} />
                      <Route path="/english-learning/grammar" element={withCanonical("/english-learning/grammar", <EnglishGrammar />)} />
                      <Route path="/english-learning/sentence-building" element={withCanonical("/english-learning/sentence-building", <SentenceBuilding />)} />
                      <Route path="/sentences-of-the-day" element={withCanonical("/sentences-of-the-day", <SentencesOfTheDay />)} />
                      <Route path="/english-learning/sight-words" element={withCanonical("/english-learning/sight-words", <SightWords />)} />
                      <Route path="/english-learning/daily-activities" element={withCanonical("/english-learning/daily-activities", <DailyActivities />)} />
                      {/* Redirect old English routes to new nested paths */}
                      <Route path="/english-grammar" element={<Navigate to="/english-learning/grammar" replace />} />
                      <Route path="/sentence-building" element={<Navigate to="/english-learning/sentence-building" replace />} />
                      <Route path="/sight-words" element={<Navigate to="/english-learning/sight-words" replace />} />
                      <Route path="/measurement-basics" element={withCanonical("/measurement-basics", <MeasurementBasics />)} />
                      <Route path="/shapes-patterns" element={withCanonical("/shapes-patterns", <ShapesPatterns />)} />
                      <Route path="/learn-colors" element={withCanonical("/learn-colors", <LearnColors />)} />
                      <Route path="/time-money" element={withCanonical("/time-money", <TimeMoney />)} />
                      <Route path="/world-explorer" element={withCanonical("/world-explorer", <WorldExplorer />)} />
                      <Route path="/community-helpers" element={<Navigate to="/world-explorer?tab=helpers" replace />} />
                      <Route path="/history" element={<Navigate to="/world-explorer?tab=history" replace />} />
                      <Route path="/learn-about-world" element={<Navigate to="/world-explorer?tab=world" replace />} />
                      <Route path="/gk-history" element={withCanonical("/gk-history", <GKHistory />)} />
                      <Route path="/printables-zone" element={withCanonical("/printables-zone", <PrintablesZone />)} />
                      <Route path="/printables" element={<Navigate to="/printables-zone" replace />} />
                      <Route path="/pricing" element={withCanonical("/pricing", <Pricing />)} />
                      <Route path="/yt-gallery" element={withCanonical("/yt-gallery", <YtGallery />)} />
                      <Route path="/admin-portal-2024" element={withCanonical("/admin-portal-2024", <AdminLogin />)} />
                      {/* Convenience alias */}
                      <Route path="/admin" element={<Navigate to="/admin-portal-2024" replace />} />
                      <Route path="/admin-dashboard" element={withCanonical("/admin-dashboard", <AdminDashboard />)} />
                      <Route path="/reset-password" element={withCanonical("/reset-password", <ResetPassword />)} />
                      <Route path="/privacy-policy" element={withCanonical("/privacy-policy", <PrivacyPolicy />)} />
                      <Route path="/terms-of-service" element={withCanonical("/terms-of-service", <TermsOfService />)} />         
                      <Route path="/blog" element={withCanonical("/blog", <Blog />)} />
                      {/* ✅ Dynamic blog route */}
                      <Route path="/blog/:slug" element={<BlogCanonicalWrapper />} />
                      <Route path="/auth/callback" element={withCanonical("/auth/callback", <MagicLinkCallback />)} />
                      <Route path="/parent-dashboard" element={withCanonical("/parent-dashboard", <ParentDashboard />)} />
                      <Route path="/parent-dashboard/subject/:category" element={<SubjectDetails />} />
                      <Route path="/dashboard" element={<Navigate to="/parent-dashboard" replace />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                    </Suspense>
                  </ErrorBoundary>
                </AuthCheckWrapper>
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;

